// src/pages/seller/SellerChatPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Send, User } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { db } from '../../utils/firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';

const SellerChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  // const [chatData, setChatData] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!user?.uid || user.userType !== 'seller') {
      navigate('/seller/messages');
      return;
    }

    const loadChat = async () => {
      try {
        const chatDocRef = doc(db, 'messages', chatId);
        const chatSnap = await getDoc(chatDocRef);
        if (!chatSnap.exists() || chatSnap.data().sellerId !== user.uid) {
          alert('Chat not found or access denied.');
          navigate('/seller/messages');
          return;
        }
        const chat = { id: chatSnap.id, ...chatSnap.data() };
        // setChatData(chat);

        const buyerDoc = await getDoc(doc(db, 'users', chat.buyerId));
        if (buyerDoc.exists()) {
          setBuyer(buyerDoc.data());
        }

        const messagesRef = collection(db, 'messages', chatId, 'messages');
        const q = query(messagesRef);
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const msgs = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => a.timestamp?.seconds - b.timestamp?.seconds);
          setMessages(msgs);
          setLoading(false);

          if (chat.unreadCount > 0) {
            updateDoc(chatDocRef, { unreadCount: 0 });
          }
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('Error loading chat:', err);
        alert('Failed to load conversation.');
        navigate('/seller/messages');
      }
    };

    loadChat();
  }, [chatId, user?.uid, user?.userType, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatId) return;
    try {
      await addDoc(collection(db, 'messages', chatId, 'messages'), {
        senderId: user.uid,
        text: newMessage.trim(),
        timestamp: serverTimestamp()
      });
      setNewMessage('');

      await updateDoc(doc(db, 'messages', chatId), {
        lastMessage: newMessage.trim(),
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Send error:', err);
      alert('Failed to send message.');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading conversation...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-[80vh]">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-gray-200 rounded-full w-10 h-10 flex items-center justify-center">
              <User size={18} className="text-gray-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {buyer?.displayName || 'Anonymous Buyer'}
              </h2>
              <p className="text-sm text-gray-500">
                {buyer?.company || 'No company'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/seller/messages')}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-3">
          {messages.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No messages yet. Start the conversation!</p>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`max-w-xs p-3 rounded-lg ${
                  msg.senderId === user.uid
                    ? 'bg-blue-500 text-white ml-auto'
                    : 'bg-gray-200 text-gray-800'
                }`}
              >
                <p>{msg.text}</p>
                <p className="text-xs opacity-80 mt-1 text-right">
                  {msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-gray-200 flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerChatPage;